import * as React from "react";
import type { RefObject } from "react";
import type { IRichToolTipShowOptions, IRichToolTipStore, RichToolTipContentType, RichToolTipPosition } from "./RichToolTipStore";
import { observer } from "mobx-react";
import { nullOrUndefined } from "../../util/functions/nullOrUndefined";
import { injectFromApp } from "../inject";
import { IResourcesStore } from "../../resources/ResourcesStore";

export type RichToolTipComponentProps<TElement extends HTMLElement, TAttributes extends React.HTMLAttributes<TElement>> = {
    /**
     * The content of the tooltip to be shown.
     * This content will be rendered as a React component (JSX element), or as plain text (string).
     */
    toolTipContent?: RichToolTipContentType | null;
    /**
     * !!! Avoid using this property, use toolTipContent instead !!!
     * !!! This method is prone to XSS attacks !!!
     * !!! If you must use this property, make sure to sanitize the content !!!
     * The HTML content of the tooltip to be shown. This content will be rendered as HTML.
     */
    toolTipDangerousHtmlContent?: { __html: string } | null;

    /**
     * The resource key of the tooltip to be shown.
     * The content of the tooltip will be taken from the resources
     */
    toolTipResource?: keyof IResources;

    /**
     * The resource parameters to be used when getting the resource content.
     */
    toolTipResourceParams?: string[];

    /**
     * The position of the tooltip relative to the element.
     * The tooltip will be displayed at the top, bottom, left or right of the element.
     * Default value is "top".
     */
    toolTipPosition?: RichToolTipPosition;

    /**
     * Options to customize the tooltip.
     */
    toolTipOptions?: IRichToolTipShowOptions;

    /**
     * The delay in milliseconds before the tooltip is shown.
     * Default value is 0.
     */
    toolTipDelay?: number;

    /**
     * The margin in pixels between the tooltip and the element.
     * Default value is 10.
     */
    toolTipMargin?: number;

    /**
     * Will be injected, no need to provide.
     */
    richToolTipStore?: IRichToolTipStore;

    /**
     * Will be injected, no need to provide.
     */
    resourcesStore?: IResourcesStore;
} & TAttributes;

export type RichToolTipGenericComponentProps<TElement extends HTMLElement, TAttributes extends React.HTMLAttributes<TElement>> = {
    tag: string;
} & RichToolTipComponentProps<TElement, TAttributes>;

@injectFromApp(stores => ({
    richToolTipStore: stores.richToolTipStore,
    resourcesStore: stores.resourcesStore,
}))

@observer
export class RichToolTipGenericComponent<TElement extends HTMLElement, TAttributes extends React.HTMLAttributes<TElement>>
    extends React.Component<RichToolTipGenericComponentProps<TElement, TAttributes>> {

    private readonly containerDivRef: RefObject<TElement>;

    constructor(props) {
        super(props);
        this.containerDivRef = React.createRef();
    }

    componentWillUnmount() {
        const { props } = this;
        const { richToolTipStore } = props;

        richToolTipStore!.hideToolTip();
    }

    onPointerEnter = (event: React.PointerEvent<TElement>) => {
        const { props, containerDivRef } = this;
        const {
            richToolTipStore,
            toolTipContent,
            toolTipResource,
            toolTipResourceParams,
            toolTipDangerousHtmlContent,
            toolTipPosition,
            toolTipOptions,
            toolTipDelay,
            toolTipMargin,
            resourcesStore
        } = props;

        const content = toolTipResource
            ? { __html: resourcesStore!.getFormattedResource(toolTipResource, toolTipResourceParams) }
            : toolTipContent ?? toolTipDangerousHtmlContent;

        if (nullOrUndefined(content)) return;

        richToolTipStore!.showToolTip(
            content,
            toolTipPosition ?? "top",
            containerDivRef,
            toolTipOptions,
            toolTipDelay,
            toolTipMargin
        );

        if (props.onPointerEnter)
            props.onPointerEnter(event);
    };

    onPointerLeave = (event: React.PointerEvent<TElement>) => {
        const { props } = this;
        const { richToolTipStore } = props;

        richToolTipStore!.hideToolTip();

        if (props.onPointerLeave) props.onPointerLeave(event);
    };

    render() {
        const {
            children,
            toolTipContent,
            richToolTipStore,
            toolTipPosition,
            toolTipOptions,
            toolTipDelay,
            toolTipMargin,
            tag,
            resourcesStore,
            toolTipDangerousHtmlContent,
            toolTipResource,
            toolTipResourceParams,
            ...propsToPass
        } = this.props;
        return React.createElement(
            tag,
            {
                ref: this.containerDivRef,
                ...propsToPass,
                onPointerEnter: this.onPointerEnter,
                onPointerLeave: this.onPointerLeave,
            },
            children,
        );
    }
}

/**
 * Acts as a <div> element which can show a tooltip
 */
export class RichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>> tag="div" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <button> element which can show a tooltip
 */
export class ButtonRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>> tag="button" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <p> element which can show a tooltip
 */
export class ParagraphRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>> tag="p" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <span> element which can show a tooltip
 */
export class SpanRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>> tag="span" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <tr> element which can show a tooltip
 */
export class TableRowRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>> tag="tr" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <th> element which can show a tooltip
 */
export class TableHeaderRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLTableHeaderCellElement, React.TdHTMLAttributes<HTMLTableHeaderCellElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLTableHeaderCellElement, React.HTMLAttributes<HTMLTableHeaderCellElement>> tag="th" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <td> element which can show a tooltip
 */
export class TableCellRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>> tag="td" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <h1> element which can show a tooltip
 */
export class Header1RichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>> tag="h1" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <h2> element which can show a tooltip
 */
export class Header2RichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>> tag="h2" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <h3> element which can show a tooltip
 */
export class Header3RichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>> tag="h3" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <h4> element which can show a tooltip
 */
export class Header4RichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLHeadElement, React.HTMLAttributes<HTMLHeadElement>> tag="h4" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <a> element which can show a tooltip
 */
export class LinkRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLLinkElement, React.LinkHTMLAttributes<HTMLLinkElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLLinkElement, React.LinkHTMLAttributes<HTMLLinkElement>> tag="a" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a &lt;img> element which can show a tooltip
 */
export class ImageRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>> tag="img" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}

/**
 * Acts as a <li> element which can show a tooltip
 */
export class ListRichToolTipComponent extends React.Component<RichToolTipComponentProps<HTMLLIElement, React.LinkHTMLAttributes<HTMLLIElement>>> {
    render() {
        const { children, ...otherProps } = this.props;
        return <RichToolTipGenericComponent<HTMLLIElement, React.LinkHTMLAttributes<HTMLLIElement>> tag="li" { ...otherProps }>
            { children }
        </RichToolTipGenericComponent>;
    }
}
